import { Bin, Item, Packer } from "@owens3364/3d-bin-packing";

/**
 * Make specific boxes by a product title, not a general box.
 * @param allItemsToPack
 */
const getSpecificProductBoxes = (allItemsToPack: ExpandedCartProduct[]) => {
    const newItemsToPack: ExpandedCartProduct[] = [];
    let finalAllItemsToPack: ExpandedCartProduct[] = [];
    finalAllItemsToPack.push(...allItemsToPack);

    SPECIFIC_PRODUCT_BOXES.forEach((specificProductBox: SpecificProductBox) => {
        let allocatedNumber = 0;
        let allocatedIds: string[] = [];

        finalAllItemsToPack.forEach((item: ExpandedCartProduct) => {
            if (specificProductBox.productType === item.productType) {
                allocatedNumber++;
                allocatedIds.push(item.id);

                // Create a specific box.
                if (allocatedNumber === specificProductBox.quantity) {
                    newItemsToPack.push({
                        id: allocatedIds.join(','),
                        productType: item.productType,
                        width: specificProductBox.widthCms,
                        height: specificProductBox.heightCms,
                        depth: specificProductBox.depthCms,
                        weight: item.weight * specificProductBox.quantity,
                        quantity: 1,
                    });

                    // Remove the packed items.
                    finalAllItemsToPack = finalAllItemsToPack.filter((item: ExpandedCartProduct) => (
                        !allocatedIds.includes(item.id)
                    ));

                    allocatedNumber = 0;
                    allocatedIds = [];
                }
            }
        });
    });

    if (newItemsToPack.length > 0) {
        finalAllItemsToPack.push(...newItemsToPack);
        return finalAllItemsToPack;
    }

    return allItemsToPack;
};

export const SPECIFIC_PRODUCT_BOXES: SpecificProductBox[] = [
    {
        productType: "sentence-train-teacher-edition",
        quantity: 3,
        widthCms: 48,
        heightCms: 16,
        depthCms: 32,
    }
];

export const POST_BOXES: PostBox[] = [
    {
        name: "size-1",
        widthCms: 23.5,
        heightCms: 7,
        depthCms: 16.5,
    },
    {
        name: "size-2",
        widthCms: 25,
        heightCms: 17,
        depthCms: 18.5,
    },
    {
        name: "size-3",
        widthCms: 35,
        heightCms: 20,
        depthCms: 26.5,
    },
    {
        name: "poster-box",
        widthCms: 63,
        heightCms: 4,
        depthCms: 42.5,
    },
    {
        name: "size-4",
        widthCms: 38,
        heightCms: 21.6,
        depthCms: 30,
    },
    {
        name: "size-5",
        widthCms: 50.7,
        heightCms: 21,
        depthCms: 31,
    },
    {
        name: "size-6",
        widthCms: 48,
        heightCms: 16,
        depthCms: 32,
    },
].sort((a: PostBox, b: PostBox) => b.widthCms * b.heightCms * b.depthCms - a.widthCms * a.heightCms * a.depthCms);

export const POST_BOXES_AU: PostBox[] = [
    {
        name: "envelope",
        widthCms: 38,
        heightCms: 7,
        depthCms: 29,
    }
];

export const runPackingProcess = (allItemsToPack: ExpandedCartProduct[], countryCode: string) => {
    const finalAllItemsToPack = getSpecificProductBoxes(allItemsToPack);

    // Ensure each item has a unique ID for tracking. We can have the same product ID with quantity > 1.
    let itemsWithTrackingId = finalAllItemsToPack.map((item, index) => ({
        ...item,
        trackingId: item.id + "_" + index,
    }));

    itemsWithTrackingId.sort(
        (a, b) => b.width * b.height * b.depth - a.width * a.height * a.depth
    );

    let unpackedItems = [...itemsWithTrackingId];
    const packedArrangements: PackedBoxes[] = [];

    // In the case of AU, the option ENVELOPE should be the first of the boxes.
    const boxes = countryCode.toLowerCase() === 'au' ? [
        ...POST_BOXES_AU,
        ...POST_BOXES
    ] : POST_BOXES;

    while (unpackedItems.length > 0) {
        let boxFilledInThisIteration = false;

        for (const boxType of boxes) {
            const packer = new Packer();
            const bin = new Bin(
                boxType.name,
                boxType.widthCms,
                boxType.heightCms,
                boxType.depthCms,
            );
            packer.addBin(bin);

            unpackedItems.forEach((itemData) => {
                packer.addItem(
                    new Item(
                        itemData.trackingId, // Use the unique trackingId as the item name for the library
                        itemData.width,
                        itemData.height,
                        itemData.depth,
                    )
                );
            });

            packer.pack();

            // If it cannot put items in one envelope, it should skip this to match the next size of box.
            if (boxType.name === 'envelope' && packer.unfitItems.length > 0) {
                continue;
            }

            // Access packed items from the bin instance.
            if (bin.items && bin.items.length > 0) {
                // Their 'name' property will be the 'trackingId' we passed.
                const packedItemTrackingIds = bin.items.map(
                    (packedLibItem) => packedLibItem.name
                );

                // Filter out original item data based on trackingId
                const itemsActuallyPackedInThisBin = itemsWithTrackingId.filter(
                    (originalItem) =>
                        packedItemTrackingIds.includes(originalItem.trackingId)
                );

                const packedItems = itemsActuallyPackedInThisBin.filter((item) =>
                    packedItemTrackingIds.includes(item.trackingId)
                );

                packedArrangements.push({
                    boxType: { ...boxType },
                    // Store a copy of the original item data, not the library's Item instances
                    items: packedItems,
                    totalWeight: packedItems.reduce((a: number, b) => a + b.weight, 0)
                });

                // Update unpackedItems: Remove items whose trackingId is in packedItemTrackingIds
                unpackedItems = unpackedItems.filter(
                    (item) => !packedItemTrackingIds.includes(item.trackingId)
                );

                boxFilledInThisIteration = true;
                break; // Move to the next physical box (i.e., start a new iteration of the while loop)
            }
        }

        if (!boxFilledInThisIteration && unpackedItems.length > 0) {
            // Add remaining items to the biggest box as we cannot pack them into any other box.
            // Forcing a break to prevent infinite loops if items genuinely can't fit.
            // We only will force 10 items to fit into the biggest box.
            const chunkSize = 10;
            for (let i = 0; i < unpackedItems.length; i+= chunkSize) {
                const chunk = unpackedItems.slice(i, i + chunkSize);
                packedArrangements.push({
                    boxType: POST_BOXES[0],
                    items: [...chunk],
                    totalWeight: chunk.reduce((a: number, b) => {
                        return a + (b.weight || 0);
                    }, 0)
                });
            }
            unpackedItems = []; // Clear to stop loop
            break;
        }
    }

    return packedArrangements;
};